import { jwtDecode } from "jwt-decode";
import {geturl} from "../components/Login";

const REFRESH_AHEAD_MS = 5 * 60 * 1000;
let refreshInFlight = null;
let sessionGeneration = 0;

function sessionKey() {
    return `${sessionGeneration}:${localStorage.getItem('cosSessionEpoch')}:${localStorage.getItem('cosSessionId')}`;
}

function withRefreshLock(rotate) {
    // localStorage is shared between tabs; an in-memory promise alone is not.
    return navigator.locks?.request ? navigator.locks.request('cos-session-refresh', rotate) : rotate();
}

function getOrCreateDeviceId() {
    const key = 'cosDeviceId';
    let value = localStorage.getItem(key);
    if (!value) {
        value = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        localStorage.setItem(key, value);
    }
    return value;
}

export function storeSession(result) {
    if (result?.token) localStorage.setItem('cosToken', result.token);
    if (result?.refresh_token) localStorage.setItem('cosRefreshToken', result.refresh_token);
    if (result?.refresh_expire_time) localStorage.setItem('cosRefreshExpireTime', String(result.refresh_expire_time));
    if (result?.session_id) localStorage.setItem('cosSessionId', result.session_id);
}

export function clearSession() {
    sessionGeneration += 1;
    refreshInFlight = null;
    localStorage.setItem('cosSessionEpoch', window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
    localStorage.removeItem('cosToken');
    localStorage.removeItem('cosRefreshToken');
    localStorage.removeItem('cosRefreshExpireTime');
    localStorage.removeItem('cosSessionId');
    window.dispatchEvent(new Event('cos-auth-changed'));
}

export async function refreshSession(force = false) {
    if (refreshInFlight) return refreshInFlight;
    const refreshToken = localStorage.getItem('cosRefreshToken');
    if (!refreshToken) return Boolean(localStorage.getItem('cosToken'));

    const currentToken = localStorage.getItem('cosToken');
    if (!force && currentToken) {
        try {
            const decoded = jwtDecode(currentToken);
            if ((Number(decoded.exp || 0) * 1000) - Date.now() > REFRESH_AHEAD_MS) return true;
        } catch (_) {
            // Refresh below.
        }
    }
    const owner = sessionKey();
    const pending = withRefreshLock(async () => {
        try {
            if (owner !== sessionKey()) return false;
            // A tab ahead of us may already have rotated this one-use token.
            if (localStorage.getItem('cosRefreshToken') !== refreshToken) {
                return Boolean(localStorage.getItem('cosToken'));
            }
            const refreshExpiry = Number(localStorage.getItem('cosRefreshExpireTime') || 0);
            if (refreshExpiry > 0 && refreshExpiry <= Date.now()) {
                clearSession();
                return false;
            }
            const response = await fetch('/api/auth/refresh', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Client-Type': 'back-office-web',
                    'X-Auth-Session': 'refresh-v1',
                    'X-Device-Id': getOrCreateDeviceId(),
                },
                body: JSON.stringify({refresh_token: refreshToken}),
            });
            if (owner !== sessionKey()) return false;
            if (localStorage.getItem('cosRefreshToken') !== refreshToken) return Boolean(localStorage.getItem('cosToken'));
            if (!response.ok) {
                // Only the refresh endpoint's 401 confirms an invalid session.
                if (response.status === 401) clearSession();
                return false;
            }
            const body = await response.json();
            if (owner !== sessionKey()) return false;
            if (localStorage.getItem('cosRefreshToken') !== refreshToken) return Boolean(localStorage.getItem('cosToken'));
            if (!body.token || !body.refresh_token) return false;
            const sessionId = localStorage.getItem('cosSessionId');
            if (sessionId && body.session_id && body.session_id !== sessionId) return false;
            storeSession(body);
            return true;
        } catch (_) {
            // Do not erase a device session for a temporary network failure.
            return false;
        }
    });
    refreshInFlight = pending;
    try {
        return await pending;
    } finally {
        if (refreshInFlight === pending) refreshInFlight = null;
    }
}

export async function sessionFetch(url, init = {}) {
    const owner = sessionKey();
    const check = () => {
        if (init.signal?.aborted) throw new DOMException('Request cancelled', 'AbortError');
        if (owner !== sessionKey()) throw new Error('Session changed during request');
    };
    check();
    const ready = await refreshSession();
    check();
    if (!ready) throw new Error('Session refresh temporarily unavailable');
    const headers = new Headers(init.headers);
    let access = localStorage.getItem('cosToken');
    const authorize = () => headers.set('Authorization', /^Bearer\s/i.test(access) ? access : `Bearer ${access}`);
    authorize();
    let response = await fetch(url, {...init, headers});
    check();
    if (response.status === 401 && localStorage.getItem('cosRefreshToken')) {
        if (access === localStorage.getItem('cosToken')) {
            const refreshed = await refreshSession(true);
            check();
            if (!refreshed) throw new Error('Session refresh temporarily unavailable');
        }
        access = localStorage.getItem('cosToken');
        authorize();
        response = await fetch(url, {...init, headers});
        check();
    } else if (response.status === 401 && access === localStorage.getItem('cosToken')) {
        // Legacy logins have no refresh session to recover.
        clearSession();
    }
    return response;
}

export async function revokeCurrentSession() {
    const refreshToken = localStorage.getItem('cosRefreshToken');
    const accessToken = localStorage.getItem('cosToken');
    clearSession();
    if (refreshToken) {
        await fetch('/api/auth/logout', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({refresh_token: refreshToken, access_token: accessToken}),
        }).catch(() => undefined);
    }
}

export function isValidLogin(role) {
    const cosToken = localStorage.getItem('cosToken');
    if (cosToken == null) return false;

    let decodedToken;
    try {
        decodedToken = jwtDecode(cosToken);
    } catch (_) {
        clearSession();
        return false;
    }

    const accessValid = Number(decodedToken.exp || 0) * 1000 > Date.now();
    const refreshToken = localStorage.getItem('cosRefreshToken');
    const refreshExpiry = Number(localStorage.getItem('cosRefreshExpireTime') || 0);
    const refreshValid = Boolean(refreshToken) && (refreshExpiry === 0 || refreshExpiry > Date.now());
    if (!accessValid && !refreshValid) return false;

    switch(decodedToken.optinestUser.optinestApiUser.role) {
        case role:
            return true;
        case 'AGENT':
        case 'PROPERTY_MANAGER':
            clearSession();
            window.location.href=geturl('manage');
            break;
        case 'ADMIN':
            clearSession();
            window.location.href=geturl('backoffice');
            break;
        case 'WORKER':
            clearSession();
            window.location.href=geturl('workers');
            break;
        default:
            clearSession();
            return false;
    }
}

export function getheaders() {
    let myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("X-COS-Job-State", "independent-important");
    let cosToken = localStorage.getItem('cosToken');
    if (!isValidLogin('ADMIN')) {
        window.location.href = "/login";
    }
    if (cosToken) myHeaders.append("Authorization", cosToken);
    return myHeaders;
}
